/**
 * Confine fra l'applicazione e il backend.
 *
 * L'intera applicazione parla con i dati attraverso due sole primitive:
 *
 *   - `select(from, query)` — lettura da una tabella o da una vista;
 *   - `rpc(fn, args)`       — esecuzione di una funzione PostgreSQL.
 *
 * Non è una limitazione arbitraria. Restringere la superficie a due operazioni
 * consente di avere implementazioni realmente equivalenti (Supabase in
 * produzione, PostgreSQL in WASM per la modalità dimostrativa e i test) e rende
 * la sostituzione del backend un lavoro circoscritto a questa cartella.
 *
 * Le scritture che coinvolgono più tabelle **non** passano da `select`: sono
 * funzioni PostgreSQL transazionali invocate con `rpc`. È ciò che rende
 * impossibile lasciare i dati a metà in caso di errore di rete.
 */

export type FilterOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'like'
  | 'ilike'
  | 'is'
  | 'contains'
  | 'overlaps';

export type ScalarValue = string | number | boolean | null;
export type FilterValue = ScalarValue | ScalarValue[];

export interface Filter {
  readonly column: string;
  readonly operator: FilterOperator;
  readonly value: FilterValue;
}

export interface OrderBy {
  readonly column: string;
  readonly ascending?: boolean;
  readonly nullsFirst?: boolean;
}

export interface SelectQuery {
  /** Colonne da leggere. Predefinito `*`. Limitare le colonne riduce i dati esposti. */
  readonly columns?: string;
  readonly filters?: readonly Filter[];
  readonly order?: readonly OrderBy[];
  readonly limit?: number;
  readonly offset?: number;
}

export type RpcArgs = Record<string, unknown>;

/** Errore normalizzato: le due implementazioni riportano gli stessi codici. */
export class DataError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
    readonly hint: string | undefined,
    readonly details: string | undefined,
  ) {
    super(message);
    this.name = 'DataError';
  }

  /** Violazione di unicità: tipicamente un'operazione già eseguita. */
  get isConflict(): boolean {
    return this.code === '23505';
  }

  /** Conflitto di concorrenza: il record è stato modificato da un altro utente. */
  get isConcurrencyConflict(): boolean {
    return this.code === '40001';
  }

  /** Autorizzazione negata. */
  get isForbidden(): boolean {
    return this.code === '42501' || this.code === 'PGRST301';
  }

  /** Violazione di una regola di dominio (check constraint o raise applicativo). */
  get isValidation(): boolean {
    return this.code === '23514' || this.code === '22023' || this.code === 'P0001';
  }

  /** Prenotazione sovrapposta: vincolo di esclusione. */
  get isOverlap(): boolean {
    return this.code === '23P01';
  }
}

export interface RealtimeSubscription {
  unsubscribe(): void;
}

export interface RealtimeOptions {
  readonly table: string;
  readonly filter?: string;
  readonly onChange: () => void;
}

export interface DataGateway {
  readonly kind: 'supabase' | 'pglite';

  select<T>(from: string, query?: SelectQuery): Promise<T[]>;
  selectOne<T>(from: string, query?: SelectQuery): Promise<T | null>;
  rpc<T>(fn: string, args?: RpcArgs): Promise<T>;

  insert<T>(into: string, values: Record<string, unknown> | Record<string, unknown>[]): Promise<T[]>;
  update<T>(table: string, values: Record<string, unknown>, filters: readonly Filter[]): Promise<T[]>;
  remove<T>(table: string, filters: readonly Filter[]): Promise<T[]>;

  /**
   * Invoca una Edge Function con l'identità dell'utente corrente. Esiste solo
   * con Supabase: il gateway PGlite la rifiuta, e chi la usa deve prevederlo.
   */
  invoke<T>(fn: string, body: Record<string, unknown>): Promise<T>;

  /**
   * Sottoscrizione agli aggiornamenti. Il gateway PGlite non ha un canale
   * realtime: restituisce una sottoscrizione inerte e le schermate ricadono
   * sul polling, che è comunque il comportamento di riserva previsto anche in
   * produzione quando la connessione realtime cade.
   */
  subscribe(options: RealtimeOptions): RealtimeSubscription;
}

/** Costruttore di filtri, per non ripetere oggetti letterali nelle query. */
export const where = {
  eq: (column: string, value: ScalarValue): Filter => ({ column, operator: 'eq', value }),
  neq: (column: string, value: ScalarValue): Filter => ({ column, operator: 'neq', value }),
  gt: (column: string, value: ScalarValue): Filter => ({ column, operator: 'gt', value }),
  gte: (column: string, value: ScalarValue): Filter => ({ column, operator: 'gte', value }),
  lt: (column: string, value: ScalarValue): Filter => ({ column, operator: 'lt', value }),
  lte: (column: string, value: ScalarValue): Filter => ({ column, operator: 'lte', value }),
  in: (column: string, value: ScalarValue[]): Filter => ({ column, operator: 'in', value }),
  ilike: (column: string, value: string): Filter => ({ column, operator: 'ilike', value }),
  is: (column: string, value: null | boolean): Filter => ({ column, operator: 'is', value }),
  contains: (column: string, value: ScalarValue[]): Filter => ({ column, operator: 'contains', value }),
} as const;
